/**
 * Building blocks of the Ayarlar screens. Three kinds of settings look different on
 * purpose: hard limits are a sunken, locked list with only a PR link; policy controls say
 * which direction loosens (a timer mark: hold plus reason) and which tightens (one tap);
 * preferences are plain one-tap controls.
 */

import { type ReactNode, useId } from 'react';
import { evaluateChange, formatPolicyValue } from '../api/policy-rules.js';
import type {
  AuditEntry,
  Connection,
  ConnectionStatus,
  FeatureFlag,
  NumberPolicy,
  PolicyChange,
  RichText,
  Settings,
  StagePolicy,
} from '../api/settings.js';
import { usePanel, useStore } from '../app/context.js';
import { useReducedMotion } from '../app/media.js';
import { Link } from '../app/router.js';
import {
  type ConnectState,
  connectState,
  holderLabel,
  revocable,
  rowAction,
  type SetupSummary,
  STATUS_LABELS,
  safeHttpsUrl,
  setupBarLabel,
} from '../app/settings.js';
import { setThemePreference, THEME_LABELS, type ThemePreference, useThemePreference } from '../app/theme.js';
import { formatClock, formatDayMonth, formatSecondsLeft } from '../lib/format.js';
import { REVOKE_HOLD_MS, useHold } from './HoldButton.js';
import { Icon } from './icons.js';
import { TriangleGlyph } from './primitives.js';
import { useSheets } from './sheets.js';

/** Text with mono parts (hosts, figures, repo names). */
export function Rich({ text }: { text: RichText }) {
  const parts = text.map((part, position) => ({ id: `r${position}`, part }));
  return (
    <>
      {parts.map(({ id, part }) =>
        typeof part === 'string' ? (
          <span key={id}>{part}</span>
        ) : (
          <span key={id} className="mono">
            {part.mono}
          </span>
        ),
      )}
    </>
  );
}

export function richToString(text: RichText): string {
  return text.map((part) => (typeof part === 'string' ? part : part.mono)).join('');
}

/** Service glyph in a 28 px tile (40 px on the detail header). Generic shapes, never brand logos. */
export function ServiceGlyph({
  connection,
  variant = 'tile',
}: {
  connection: Connection;
  variant?: 'tile' | 'bare' | 'large';
}) {
  return (
    <span className={`svc-glyph svc-glyph--${variant}`} aria-hidden="true">
      <Icon name={connection.glyph} size={variant === 'large' ? 22 : 16} />
    </span>
  );
}

function DashedCircle() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" strokeDasharray="2 1.4" />
    </svg>
  );
}

function PlainCircle() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function CircleMinus() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle cx="5" cy="5" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2.8 5H7.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

/** The pill draws "Bağlı değil" with a dashed border, so its circle is plain; filters use the dashed circle. */
export function StatusGlyph({ status, inPill = false }: { status: ConnectionStatus; inPill?: boolean }) {
  switch (status) {
    case 'awaiting_sercan':
      return <Icon name="user-round" size={10} stroke={2.25} />;
    case 'applying':
      return <span className="hatch-mark" aria-hidden="true" />;
    case 'connected':
      return <Icon name="check" size={10} stroke={2.5} className="cpill__ok" />;
    case 'not_connected':
      return inPill ? <PlainCircle /> : <DashedCircle />;
    case 'disabled':
      return <CircleMinus />;
    case 'expiring':
    case 'error':
      return <TriangleGlyph />;
  }
}

/** Connection status: shape plus word, colour third. */
export function ConnectionPill({ status }: { status: ConnectionStatus }) {
  return (
    <span className={`cpill cpill--${status}`}>
      <StatusGlyph status={status} inPill />
      {STATUS_LABELS[status]}
    </span>
  );
}

/** One segment per counted connection: solid bağlı, hatched başvuruda, accent senden, dashed bağlı değil. */
export function SetupBar({ summary }: { summary: SetupSummary }) {
  return (
    <div className="setup-bar" role="img" aria-label={setupBarLabel(summary)}>
      {summary.segments.map((segment) => (
        <span key={segment.id} className={`setup-bar__seg setup-bar__seg--${segment.kind}`} />
      ))}
    </div>
  );
}

export function SetupLegend({ summary, grid = false }: { summary: SetupSummary; grid?: boolean }) {
  return (
    <p className={grid ? 'setup-legend setup-legend--grid' : 'setup-legend'} aria-hidden="true">
      {summary.legend.map((part) => (
        <span key={part.text} className="setup-legend__item">
          <span className={`setup-legend__swatch setup-bar__seg--${part.kind}`} />
          <span className={part.kind === 'accent' ? 'setup-legend__strong' : undefined}>{part.text}</span>
        </span>
      ))}
    </p>
  );
}

/** Collapsible section header on the phone: title on an ink rule, hint on the right. */
export function SectionToggle({
  id,
  title,
  count,
  hint,
  lock = false,
  expanded,
  onToggle,
}: {
  id: string;
  title: string;
  count?: number | null;
  hint: string;
  lock?: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <h2 id={id} className="st-head">
      <button
        type="button"
        className="st-head__btn"
        aria-expanded={expanded}
        aria-controls={`${id}-govde`}
        onClick={onToggle}
      >
        <span className="st-head__title">{title}</span>
        {lock ? <Icon name="lock" size={14} className="ink-2 st-head__lock" /> : null}
        {count !== undefined && count !== null ? <span className="st-head__count mono">{count}</span> : null}
        <span className="st-head__hint">{hint}</span>
        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={18} className="ink-3 st-head__chev" />
      </button>
    </h2>
  );
}

/** Opens the loosen sheet or applies a tightening change in one tap; refuses with the reason. */
export function usePolicyAction() {
  const store = useStore();
  const sheets = useSheets();
  const { settings } = usePanel();
  return (change: PolicyChange) => {
    if (!settings) return;
    const evaluation = evaluateChange(settings, change);
    if (evaluation.violation) {
      store.showToast(evaluation.violation.message, { tone: 'danger', icon: 'alert' });
      return;
    }
    if (evaluation.direction === 'loosen') sheets.open({ kind: 'loosen', change });
    else if (evaluation.direction === 'tighten') void store.changePolicy(change);
  };
}

function directionHint(direction: 'tighten' | 'loosen' | 'none'): string {
  return direction === 'loosen' ? 'gevşetir, 1,5 saniye basılı tut ve gerekçe yaz' : 'sıkılaştırır, tek dokunuş';
}

/** [−] value [+]: the loosening side carries a timer mark (hold plus reason), the other is one tap. */
export function PolicyStepper({ policy, labelId }: { policy: NumberPolicy; labelId: string }) {
  const { settings } = usePanel();
  const store = useStore();
  const act = usePolicyAction();
  if (!settings) return null;
  const side = (value: number, word: 'düşür' | 'artır') => {
    const change: PolicyChange = { kind: 'number', id: policy.id, value };
    const evaluation = evaluateChange(settings, change);
    const outOfRange = value < policy.min || value > policy.max;
    const blocked = evaluation.violation;
    const label = outOfRange
      ? `${policy.label}: ${word}, ${word === 'düşür' ? 'en düşük' : 'en yüksek'} değerde`
      : `${policy.label}: ${word} (${formatPolicyValue(policy, value)}), ${directionHint(evaluation.direction)}${
          blocked ? `. Kapalı: ${blocked.message}` : ''
        }`;
    return {
      change,
      loosens: evaluation.direction === 'loosen',
      outOfRange,
      blocked: blocked !== null,
      label,
      onClick: () => {
        if (outOfRange) return;
        if (blocked) store.showToast(blocked.message, { tone: 'danger', icon: 'alert' });
        else act(change);
      },
    };
  };
  const down = side(policy.value - policy.step, 'düşür');
  const up = side(policy.value + policy.step, 'artır');
  return (
    // biome-ignore lint/a11y/useSemanticElements: a stepper is a labelled group of two buttons and an output, not a fieldset form.
    <span role="group" aria-labelledby={labelId} className="stepper">
      <button
        type="button"
        className="stepper__btn stepper__btn--down"
        aria-label={down.label}
        disabled={down.outOfRange}
        aria-disabled={down.blocked || undefined}
        onClick={down.onClick}
      >
        <Icon name="minus" size={16} />
        {down.loosens ? <Icon name="timer" size={12} className="ink-2" /> : null}
      </button>
      <output className="stepper__value mono">{formatPolicyValue(policy, policy.value)}</output>
      <button
        type="button"
        className="stepper__btn stepper__btn--up"
        aria-label={up.label}
        disabled={up.outOfRange}
        aria-disabled={up.blocked || undefined}
        onClick={up.onClick}
      >
        <Icon name="plus" size={16} />
        {up.loosens ? <Icon name="timer" size={12} className="ink-2" /> : null}
      </button>
    </span>
  );
}

export function SwitchVisual({ on, locked = false }: { on: boolean; locked?: boolean }) {
  return (
    <span className={`switch${on ? ' switch--on' : ''}${locked ? ' switch--locked' : ''}`} aria-hidden="true">
      <span className="switch__knob">{locked ? <Icon name="lock" size={10} stroke={2} /> : null}</span>
    </span>
  );
}

function stateWord(enabled: boolean): string {
  return enabled ? 'Açık' : 'Kapalı';
}

/**
 * Feature flag switch. Away from the safe state is loosening (hold plus reason); back to it
 * is one tap. Unmet prerequisites lock the switch and say why; it stays focusable.
 */
export function FlagRow({ flag, desktop = false }: { flag: FeatureFlag; desktop?: boolean }) {
  const { settings } = usePanel();
  const store = useStore();
  const act = usePolicyAction();
  if (!settings) return null;
  const change: PolicyChange = { kind: 'flag', id: flag.id, enabled: !flag.enabled };
  const evaluation = evaluateChange(settings, change);
  const locked = evaluation.violation !== null;
  const verb = flag.enabled ? 'Kapatmak' : 'Açmak';
  const aria = `${flag.label}: ${stateWord(flag.enabled).toLocaleLowerCase('tr')}${
    locked
      ? `, kilitli. ${evaluation.violation?.message ?? ''}`
      : `. ${verb} ${evaluation.direction === 'loosen' ? 'gevşetir; 1,5 saniye basılı tut ve gerekçe yaz' : 'sıkılaştırır, tek dokunuş'}`
  }`;
  const unmet = flag.prerequisites.filter((item) => !item.met);
  return (
    <div className={desktop ? 'pol-row pol-row--flag pol-row--desk' : 'pol-row pol-row--flag'}>
      <span className="pol-row__text">
        <span className="pol-row__title pol-row__title--small">{flag.label}</span>
        <span className="pol-row__sub">
          <span className="pol-row__state">{stateWord(flag.enabled)}</span> ·{' '}
          {flag.enabled ? flag.onNote : flag.offNote}
        </span>
        {desktop && unmet.length > 0 ? (
          <span className="prereqs">
            {unmet.map((item) => (
              <span key={item.label} className="prereqs__item">
                <Icon name="lock" size={10} stroke={2} />
                {item.label}
                {item.progress ? <span className="mono"> {item.progress}</span> : null}
              </span>
            ))}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={flag.enabled}
        aria-disabled={locked || undefined}
        aria-label={aria}
        className="switch-hit"
        onClick={() => {
          if (evaluation.violation) store.showToast(evaluation.violation.message, { tone: 'danger', icon: 'alert' });
          else act(change);
        }}
      >
        <SwitchVisual on={flag.enabled} locked={locked} />
      </button>
    </div>
  );
}

/** Mail autonomy stages: the current one checked, locked stages show their condition. */
export function StageControl({ stage }: { stage: StagePolicy }) {
  const store = useStore();
  const act = usePolicyAction();
  const name = useId();
  return (
    <fieldset className="seg-radio seg-radio--stage">
      <legend className="sr-only">{stage.label} aşaması</legend>
      {stage.stages.map((option) => {
        const locked = !option.unlocked && option.value > stage.value;
        const checked = option.value === stage.value;
        return (
          <label
            key={option.value}
            className={`seg-radio__opt${checked ? ' seg-radio__opt--on' : ''}${locked ? ' seg-radio__opt--locked' : ''}`}
          >
            <input
              type="radio"
              className="seg-radio__input"
              name={name}
              value={option.value}
              checked={checked}
              aria-disabled={locked || undefined}
              aria-label={
                locked
                  ? `Aşama ${option.value}, kilitli: ${option.condition} gerekir`
                  : `Aşama ${option.value}: ${option.label}`
              }
              onChange={() => {
                if (locked) {
                  store.showToast(`Aşama ${option.value} kilitli: ${option.condition}`, { icon: 'info' });
                  return;
                }
                act({ kind: 'stage', id: stage.id, value: option.value });
              }}
            />
            {locked ? <Icon name="lock" size={10} stroke={2} /> : null}
            <span className="mono">{option.value}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

/** Sistem / Açık / Koyu: a device preference, applied at once. */
export function ThemeRadio() {
  const preference = useThemePreference();
  const name = useId();
  return (
    <fieldset className="seg-radio seg-radio--theme">
      <legend className="sr-only">Tema</legend>
      {(['system', 'light', 'dark'] as const).map((id) => (
        <label key={id} className={`seg-radio__opt${preference === id ? ' seg-radio__opt--on' : ''}`}>
          <input
            type="radio"
            className="seg-radio__input"
            name={name}
            value={id}
            checked={preference === id}
            onChange={() => setThemePreference(id as ThemePreference)}
          />
          {THEME_LABELS[id]}
        </label>
      ))}
    </fieldset>
  );
}

/** A free preference switch: one tap either way, no reason. */
export function PrefSwitch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="pol-row pol-row--pref">
      <span className="pol-row__text">
        <span className="pol-row__title">{label}</span>
        <span className="pol-row__sub">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`${label}: ${checked ? 'açık' : 'kapalı'}. Tek dokunuşla değişir`}
        className="switch-hit"
        onClick={() => onChange(!checked)}
      >
        <SwitchVisual on={checked} />
      </button>
    </div>
  );
}

/** Sert sınırlar: read-only, locked; the only action is a pull request on the repo. */
export function HardLimits({ settings, showSource = false }: { settings: Settings; showSource?: boolean }) {
  return (
    <div className="hard">
      <ul className="hard__list" aria-label="Kodda kilitli kurallar; panelden değişmez">
        {settings.hardLimits.map((limit) => (
          <li key={limit.id} className={showSource ? 'hard__item hard__item--source' : 'hard__item'}>
            <Icon name="lock" size={12} stroke={2} className="ink-2 hard__lock" />
            <span className="hard__text">
              <span className="hard__rule" aria-hidden="true">
                {limit.code ? (
                  <>
                    <span className="mono hard__code">{limit.code}</span> {limit.text}
                  </>
                ) : (
                  limit.text
                )}
              </span>
              <span className="sr-only">{limit.spoken}</span>
              {showSource ? <span className="hard__source mono">{limit.enforcedIn}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      <a
        href={safeHttpsUrl(settings.repo.prUrl) ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="hard__pr"
        aria-label={`Değiştirmek için GitHub'da PR aç: ${settings.repo.name}, yeni sekmede açılır`}
      >
        <Icon name="git-pull-request" size={16} />
        Değiştirmek için GitHub PR
        <span className="mono hard__repo">{settings.repo.name.split('/')[1]}</span>
        <Icon name="external-link" size={14} className="ink-2" />
      </a>
    </div>
  );
}

/**
 * Bağla: opens the connect flow on the Twin backend in a new tab. The consent page hands
 * the grant straight to the executor; the panel never handles a token.
 */
export function ConnectLink({
  connection,
  state,
  className,
  children,
}: {
  connection: Connection;
  state: Extract<ConnectState, { kind: 'connect' }>;
  className: string;
  children: ReactNode;
}) {
  const store = useStore();
  return (
    <a
      href={state.url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={`${state.label}: ${connection.name}. İzin sayfası yeni sekmede açılır, yetki doğrudan ${holderLabel(
        connection.tokenHolder,
      ).toLocaleLowerCase('tr')} tarafına gider`}
      onClick={() => store.noteConnectOpened(connection)}
    >
      {children}
    </a>
  );
}

/** The one primary action of a connection row: Bağla, Yenile, Başvuruyu aç or Ayrıntı. */
export function RowAction({
  connection,
  primary = false,
  small = false,
}: {
  connection: Connection;
  primary?: boolean;
  small?: boolean;
}) {
  const action = rowAction(connection);
  const className = `row-btn${primary ? ' row-btn--primary' : ''}${small ? ' row-btn--small' : ''}`;
  if (action.kind === 'connect') {
    const state = connectState(connection);
    if (state.kind === 'connect') {
      return (
        <ConnectLink connection={connection} state={state} className={className}>
          {action.label}
        </ConnectLink>
      );
    }
  }
  if (action.kind === 'apply') {
    return (
      <a
        href={action.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} row-btn--ext`}
        aria-label={`${connection.name} başvurusunu aç, yeni sekmede`}
      >
        Başvuruyu aç
        <Icon name="external-link" size={12} />
      </a>
    );
  }
  return (
    <Link
      to={`/ayarlar/baglanti/${connection.id}`}
      className={className}
      aria-label={`${connection.name} bağlantısının ayrıntısı${connection.status === 'disabled' ? ': neden kapalı' : ''}`}
    >
      Ayrıntı
    </Link>
  );
}

/** Tüm yetkileri iptal et: 2 s hold, danger; closed while there is no grant to revoke. */
export function RevokeButton({ connection }: { connection: Connection }) {
  const store = useStore();
  const reducedMotion = useReducedMotion();
  const enabled = revocable(connection);
  const { holding, remaining, handlers } = useHold({
    durationMs: REVOKE_HOLD_MS,
    disabled: !enabled,
    onComplete: () => void store.revokeConnection(connection.id, { confirmedByHold: true }),
    onEarlyRelease: () => store.showToast('Yetkileri iptal etmek için 2 sn basılı tut', { icon: 'info' }),
  });
  const content = (
    <>
      <Icon name="unplug" size={18} />
      <span className="revoke__label">{holding ? 'Basılı tut…' : 'Tüm yetkileri iptal et'}</span>
      <span className={holding ? 'revoke__hint mono' : 'revoke__hint key'}>
        {holding ? formatSecondsLeft(remaining) : '2 sn basılı tut'}
      </span>
    </>
  );
  return (
    <button
      type="button"
      className={`revoke${enabled ? ' revoke--armed' : ''}${holding ? ' revoke--holding' : ''}`}
      disabled={!enabled}
      aria-label={
        !enabled
          ? 'Tüm yetkileri iptal et: 2 saniye basılı tutulur. Şu an kapalı, iptal edilecek yetki yok'
          : holding
            ? `Tüm yetkileri iptal et: basılı tutuluyor, ${formatSecondsLeft(remaining)} kaldı`
            : `Tüm yetkileri iptal et: 2 saniye basılı tut. Token silinir, ${connection.name} izni geri alınır`
      }
      style={{ ['--hold-ms' as string]: `${REVOKE_HOLD_MS}ms` }}
      {...handlers}
    >
      <span className="revoke__content">{content}</span>
      {holding && !reducedMotion ? (
        <span className="hold-fill hold-fill--danger" aria-hidden="true">
          <span className="revoke__content revoke__content--on-fill">{content}</span>
        </span>
      ) : null}
    </button>
  );
}

export function DirectionBadge({ direction }: { direction: AuditEntry['direction'] }) {
  if (direction === 'loosen') return <span className="dir dir--loosen">Gevşetildi</span>;
  if (direction === 'tighten') return <span className="dir dir--tighten">Sıkılaştırıldı</span>;
  return <span className="dir">Kayıt</span>;
}

/** Değişiklik kaydı rows: when, what, from → to, direction, reason. */
export function AuditList({ entries, empty }: { entries: AuditEntry[]; empty: string }) {
  if (entries.length === 0) {
    return (
      <div className="audit-empty">
        <Icon name="history" size={16} className="ink-3" />
        <span>{empty}</span>
      </div>
    );
  }
  return (
    <ol className="audit-list">
      {entries.map((entry) => (
        <li key={entry.id} className="audit-list__item">
          <span className="audit-list__time mono">
            {formatDayMonth(entry.at)} {formatClock(entry.at)}
          </span>
          <span className="audit-list__body">
            <span className="audit-list__what">
              <span className="audit-list__target">{entry.targetLabel}</span> {entry.change}
            </span>
            <span className="audit-list__meta">
              <DirectionBadge direction={entry.direction} />
              <span>{entry.actor}</span>
              {entry.reason ? <span className="audit-list__reason">“{entry.reason}”</span> : null}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}
